import moment from "moment";
import { useEffect, useState } from "react";
import getCurrentUser from "../../../../../../../utils/getCurrentUser"; // Adjust the path if needed

export const useRatePlans = ({
  fromdate,
  todate,
  ratePlans,
  room,
  promotions,
  promoDiscount,
  promoCode,
}) => {
  const [rates, setRates] = useState({});
  const user = getCurrentUser();

  useEffect(() => {
    const calculateRates = () => {
      const calculatedRates = {};
      const today = moment(); // Booking date (today)

      if (fromdate === todate) {
        setRates({});
        return;
      }

      if (Array.isArray(ratePlans)) {
        ratePlans.forEach((ratePlan) => {
          const pricing = ratePlan.rateplanpricing.find(
            (p) => p.roomId === room._id
          );
          let totalRate = 0;
          let originalTotalRate = 0;
          let hasValidRate = true;
          const start = moment(fromdate, "DD-MM-YYYY");
          const end = moment(todate, "DD-MM-YYYY").subtract(1, "days");
          const days = end.diff(start, "days") + 1;

          // Calculate the base total rate for the date range
          for (let i = 0; i < days; i++) {
            const dateStr = moment(fromdate, "DD-MM-YYYY")
              .add(i, "days")
              .format("DD-MM-YYYY");
            let dailyRate = 0;

            if (ratePlan.ratePlanSetup === "Derived") {
              // determine the base rate from room specific date rate or default rentperday
              const baseRate =
                room.rentperdayByDate?.[dateStr] ?? room.rentperday ?? 0;
              // apply ratePlan level discount if provided (expects discountPercentage on ratePlan)
              const discountPercentage = Number(ratePlan.discount || 0);
              dailyRate = baseRate * (1 - discountPercentage / 100);
            } else if (
              pricing &&
              pricing.manualRate &&
              pricing.manualRate[dateStr] !== undefined
            ) {
              dailyRate = pricing.manualRate[dateStr];
            } else {
              hasValidRate = false;
              break;
            }

            totalRate += dailyRate;
            originalTotalRate += dailyRate;
          }

          if (hasValidRate) {
            let discountedRate = totalRate;
            const appliedPromotions = [];
            const checkInDate = moment(fromdate, "DD-MM-YYYY");

            // Step 1: Collect all applicable promotions (excluding Promo Code)
            const applicablePromotions = [];
            promotions.forEach((promotion) => {
              const appliesToRoom = promotion.applicableRooms.includes(
                room._id
              );
              const appliesToRatePlan = promotion.applicableRatePlans.includes(
                ratePlan._id
              );
              if (!appliesToRoom || !appliesToRatePlan) return;

              const bookStart = moment(promotion.applicableBookDates.startDate);
              const bookEnd = moment(promotion.applicableBookDates.endDate);
              const stayStart = moment(promotion.applicableStayDates.startDate);
              const stayEnd = moment(promotion.applicableStayDates.endDate);

              const isWithinBookingPeriod = today.isBetween(
                bookStart,
                bookEnd,
                null,
                "[]"
              );
              const isWithinStayPeriod = checkInDate.isBetween(
                stayStart,
                stayEnd,
                null,
                "[]"
              );
              const daysBeforeCheckIn = checkInDate
                .startOf("day")
                .diff(today.startOf("day"), "days");

              if (!isWithinBookingPeriod || !isWithinStayPeriod) return;

              switch (promotion.promotionType) {
                case "Last Minute": {
                  const lastMinuteDays =
                    promotion.condition?.lastMinuteDays || 3;
                  if (
                    daysBeforeCheckIn >= 0 &&
                    daysBeforeCheckIn <= lastMinuteDays
                  ) {
                    applicablePromotions.push({
                      type: promotion.promotionType,
                      percentage: promotion.discountPercentage,
                    });
                  }
                  break;
                }
                case "Early Bird": {
                  const earlyBirdDays =
                    promotion.condition?.earlyBirdDays || 30;
                  if (daysBeforeCheckIn >= earlyBirdDays) {
                    applicablePromotions.push({
                      type: promotion.promotionType,
                      percentage: promotion.discountPercentage,
                    });
                  }
                  break;
                }
                case "Basic Deal":
                case "Seasonal":
                case "Special Offer":
                  applicablePromotions.push({
                    type: promotion.promotionType,
                    percentage: promotion.discountPercentage,
                  });
                  break;
                case "Member":
                  // Only apply if user is logged in (user object exists)
                  if (user && user._id) {
                    applicablePromotions.push({
                      type: promotion.promotionType,
                      percentage: promotion.discountPercentage,
                    });
                  }
                  break;
                default:
                  break;
              }
            });

            // Step 2: Select the promotion with the highest discount percentage (excluding Promo Code)
            if (applicablePromotions.length > 0) {
              const highestDiscountPromotion = applicablePromotions.reduce(
                (maxPromo, currentPromo) =>
                  currentPromo.percentage > maxPromo.percentage
                    ? currentPromo
                    : maxPromo,
                applicablePromotions[0]
              );
              discountedRate *= 1 - highestDiscountPromotion.percentage / 100;
              appliedPromotions.push(highestDiscountPromotion);
            }

            // Step 3: Apply Promo Code discount progressively, if it exists and is applicable
            if (
              promoDiscount &&
              promoDiscount.applicableRooms.includes(room._id) &&
              promoDiscount.applicableRatePlans.includes(ratePlan._id)
            ) {
              discountedRate *= 1 - promoDiscount.discountPercentage / 100;
              appliedPromotions.push({
                type: "Promo Code",
                name: promoCode,
                percentage: promoDiscount.discountPercentage,
              });
            }

            // Store the calculated rates for this rate plan
            if (discountedRate > 0) {
              calculatedRates[ratePlan.rateplanname] = {
                totalRate: discountedRate,
                originalTotalRate,
                ratePlanId: ratePlan._id,
                ratePlanName: ratePlan.rateplanname,
                selectedRooms: 0,
                roomId: room._id,
                cancellationPolicy: ratePlan.cancellationPolicy,
                mealPlan: ratePlan.mealPlan,
                appliedPromotions,
              };
            }
          }
        });

        // Sort calculatedRates by totalRate from low to high and preserve insertion order
        const sortedEntries = Object.entries(calculatedRates).sort(
          (a, b) => a[1].totalRate - b[1].totalRate
        );
        const orderedCalculatedRates = Object.fromEntries(sortedEntries);
        try {
          const prev = JSON.stringify(rates || {});
          const next = JSON.stringify(orderedCalculatedRates || {});
          if (prev !== next) {
            setRates(orderedCalculatedRates);
          }
        } catch (err) {
          setRates(orderedCalculatedRates);
        }
      }
    };

    if (fromdate && todate) {
      calculateRates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromdate, todate, ratePlans, room, promotions, promoDiscount, promoCode]);

  return {
    rates,
  };
};
